import React from 'react';
import { motion } from 'framer-motion';
import { Wifi, Download, Upload, Zap } from 'lucide-react';
import { useWirelessAdvisor } from '../../context/WirelessAdvisorContext';
import QuestionTooltip from '../shared/QuestionTooltip';
import { trackEvent } from '../../../../utils/analytics';

/**
 * InternetSpeed component
 * Final step in the wireless advisor form
 * Allows users to specify their internet connection speed
 */
const InternetSpeed = () => {
  const { formData, updateFormData } = useWirelessAdvisor();
  
  // Handle internet speed selection
  const handleInternetSpeedSelect = (internetSpeed) => {
    // Update form data
    updateFormData({
      internetSpeed
    });
    
    // Track event
    trackEvent('wireless_advisor_internet_speed_selected', {
      internet_speed: internetSpeed
    });
  };
  
  // Internet speeds
  const internetSpeeds = [
    { id: 'slow', label: 'Basic (< 50 Mbps)', description: 'ADSL or basic fiber', icon: <Download /> },
    { id: 'medium', label: 'Standard (50-200 Mbps)', description: 'Standard fiber connection', icon: <Wifi /> },
    { id: 'fast', label: 'Fast (200-500 Mbps)', description: 'High-speed fiber', icon: <Upload /> },
    { id: 'ultrafast', label: 'Ultra-Fast (500+ Mbps)', description: 'Premium fiber connection', icon: <Zap /> }
  ];
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">Internet Connection</h2>
        <QuestionTooltip 
          content="Your internet connection speed affects the router specifications you need. Faster connections require more capable routers."
          iconSize="md"
        />
      </div>
      
      <p className="text-gray-400 text-sm">
        What is your current internet connection speed? This helps us recommend the right wireless setup.
      </p>
      
      {/* Internet Speed */}
      <div className="space-y-3">
        <div className="flex items-center">
          <label className="block text-sm font-medium text-gray-300 mr-2">
            Select your internet connection speed
          </label>
          <QuestionTooltip 
            content="Your internet connection speed affects the router specifications you need. Faster connections require more capable routers."
            iconSize="sm"
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {internetSpeeds.map((speed) => (
            <motion.button
              key={speed.id}
              onClick={() => handleInternetSpeedSelect(speed.id)}
              className={`p-4 rounded-lg border-2 transition-all ${
                formData.internetSpeed === speed.id
                  ? 'border-purple-600 bg-purple-600/10'
                  : 'border-gray-700 hover:border-purple-500/50 bg-gray-800/50'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center">
                <div className={`p-2 rounded-lg ${formData.internetSpeed === speed.id ? 'bg-purple-600/20' : 'bg-gray-700'} mr-3`}>
                  {React.cloneElement(speed.icon, { 
                    className: `h-5 w-5 ${formData.internetSpeed === speed.id ? 'text-purple-400' : 'text-gray-400'}`
                  })}
                </div>
                <div className="text-left">
                  <span className={`font-medium ${formData.internetSpeed === speed.id ? 'text-purple-400' : 'text-white'}`}>
                    {speed.label}
                  </span>
                  <p className="text-xs text-gray-400 mt-1">
                    {speed.description}
                  </p>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>
      
      {/* Speed Explanation */}
      {formData.internetSpeed && (
        <motion.div
          className="mt-6 p-4 bg-purple-600/10 border border-purple-600/20 rounded-lg"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <h3 className="text-lg font-medium text-purple-400 mb-2">
            What This Means For Your WiFi
          </h3>
          
          {formData.internetSpeed === 'slow' && (
            <p className="text-sm text-gray-300">
              With a basic internet connection, you'll need a router that can reliably distribute your connection throughout your home. 
              A standard mesh system will be sufficient for your needs.
            </p>
          )}
          
          {formData.internetSpeed === 'medium' && (
            <p className="text-sm text-gray-300">
              Your standard fiber connection requires a good quality mesh system to ensure you get the full speed 
              in all areas of your home. We'll recommend a setup that can handle your connection speed.
            </p>
          )}
          
          {formData.internetSpeed === 'fast' && (
            <p className="text-sm text-gray-300">
              Your high-speed connection needs a high-performance mesh system to ensure you get the full benefit 
              of your fast internet throughout your home. We'll recommend a setup with the right specifications.
            </p>
          )}
          
          {formData.internetSpeed === 'ultrafast' && (
            <p className="text-sm text-gray-300">
              Your ultra-fast connection requires a premium mesh system with the latest technology to fully 
              utilize your connection speed. We'll recommend a high-end setup that can handle your premium connection.
            </p>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default InternetSpeed;